<template>
  <div :class="formVariants({ theme })">
    <template v-if="useSsn || modelPerson.useLookup">
      <FormLoader :theme="theme" />

      <Input
        :label="t('personal-details.ssn')"
        v-model="modelPerson.ssn"
        :placeholder="t('ssn-placeholder')"
        :style-input="inputVariants({ theme })"
        :style-error="inputErrorVariants({ theme })"
        :v$="v$?.ssn"
        required
      >
        <template v-slot:right>
          <Check
            class="absolute right-3 top-3 lg:top-[18px]"
            v-if="modelPerson.useLookup && [THEMES.DEFAULT, THEMES.DJURENS_RATT].includes(theme)"
          />
          <CheckRDK
            class="absolute right-3 top-3 lg:top-[18px]"
            v-else-if="modelPerson.useLookup && theme === THEMES.RDK"
          />
        </template>
      </Input>
      <p :class="twMerge('mb-2 mt-2 pl-1 text-sm', theme === THEMES.RDK && 'text-sm lg:text-base')">
        {{ $t('personal-details.ssn.description') }}
      </p>
      <div class="pl-1">
        <p v-if="showSsnDisclaimer" class="mb-2 text-sm text-blue">
          Saknar du eller vill du inte uppge personnummer? Kontakta givarservice på
          <a href="mailto:givarservice@djurensratt.se" class="underline hover:cursor-pointer"
            >givarservice@djurensratt.se</a
          >
          eller
          <a href="tel:0855591440" class="hover:curesor-pointer underline">08-555 914 40</a> så
          hjälper vi dig.
        </p>
        <button v-if="!ssnRequired" :class="ssnButtonVariant({ theme })" @click="useSsn = false">
          {{ $t('personal-details.no-ssn') }}
        </button>
      </div>
    </template>

    <template v-else>
      <DatePickerFlow
        :label="t('personal-details.birth-date')"
        v-model:date="modelPerson.birthDate"
        :v$="v$?.birthDate as Validation"
        :theme="theme"
        required
        style-error="relative bg-transparent"
      />
      <p :class="twMerge('mb-7 mt-2 pl-1 text-sm', theme === THEMES.RDK && 'text-sm lg:text-base')">
        {{ t('personal-details.birth-date.description') }}
      </p>
      <div class="pl-1">
        <button :class="ssnButtonVariant({ theme })" @click="useSsn = true">
          {{ $t('personal-details.has-ssn') }}
        </button>
      </div>
    </template>
    <Input
      :label="t('personal-details.email')"
      v-model="modelPerson.contacts.email"
      :style-input="inputVariants({ theme })"
      class="mb-7"
      :required="v$?.contacts?.email?.required ? true : false"
      :v$="v$?.contacts?.email"
      style-error="relative bg-transparent"
    />
    <Input
      v-model="modelPerson.contacts.mobile"
      :label="t('personal-details.mobile')"
      :style-input="inputVariants({ theme })"
      style-error="!relative"
      :required="v$?.contacts?.mobile?.required ? true : false"
      :v$="v$?.contacts?.mobile"
    />
    <p :class="twMerge('mb-7 mt-2 pl-1 text-sm', theme === THEMES.RDK && 'text-sm lg:text-base')">
      {{ t('personal-details.mobile.description') }}
    </p>

    <Button
      v-if="siteOrg !== 'raddadjuren'"
      :title="t('button.next')"
      :arrow-right="true"
      :disabled="requireLegalAge && !isLegalAge"
      color="pink"
      class="mb-8 w-full lg:mb-2 lg:w-[315px]"
      @click="handleClickNext"
    />

    <ButtonAct
      v-if="siteOrg === 'raddadjuren'"
      :title="t('button.next')"
      :theme="theme"
      :isCta="true"
      :variant="'primary'"
      :iconType="'arrowRight'"
      @click="handleClickNext"
    />

    <div v-if="requireLegalAge && !isLegalAge">
      <p class="text-pink mt-2">
        {{ t('disclaimer-monthly-minor.text') }}
        <a href="/arsmedlem" v-if="!isChild" class="cursor-pointer underline">{{
          t('disclaimer-monthly-minor.linkText-youth')
        }}</a>
        <a
          href="https://www.raddadjuren.se/medlem"
          target="_blank"
          v-else
          class="cursor-pointer underline"
          >{{ t('disclaimer-monthly-minor.linkText-child') }}</a
        >
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from 'vue'
import { Input, Button, THEMES } from 'refresh-ui'
import { cva } from 'class-variance-authority'
import { useDebounceFn, useVModel } from '@vueuse/core'
import type { Validation } from '@vuelidate/core'
import { createPerson, type Person } from 'shared-types'
import DatePickerFlow from './DatePickerFlow.vue'
import { useI18n } from 'vue-i18n'
import Personnummer from 'personnummer'
import { zSsnLookupRes } from '@/libs/apiSchema'
import ButtonAct from '../atoms/ButtonAct.vue'
import type { SiteOrg } from '../../types/env'
import { twMerge } from 'tailwind-merge'
import Check from '../atoms/Icons/Check.vue'
import CheckRDK from '../atoms/Icons/CheckRDK.vue'
import FormLoader from '../elements/FormLoader.vue'
import { validateLegalAge } from '../../libs/age'

const { t } = useI18n()

export interface IPersonalDetailsValidation {
  ssn: string
  birthDate: string
  contacts: {
    email: string
    mobile: string
  }
}

const props = withDefaults(
  defineProps<{
    person: Person
    masked: Person
    siteOrg: SiteOrg
    useSsn?: boolean
    v$: Validation<IPersonalDetailsValidation>
    theme?: THEMES
    subTitle?: string
    ssnRequired?: boolean
    showSsnDisclaimer?: boolean
    requireLegalAge?: boolean
  }>(),
  {
    theme: THEMES.DEFAULT,
    ssnRequired: false,
    showSsnDisclaimer: false,
    requireLegalAge: false,
  },
)

const emits = defineEmits(['update:person', 'update:masked', 'onNext'])

const modelPerson = useVModel(props, 'person', emits)
const modelMasked = useVModel(props, 'masked', emits)

const { showLoader, hasResult } = useFormLoader()

const useSsn = ref(props.siteOrg === 'djurensratt' || props.useSsn ? true : false)

const isLegalAge = ref<boolean>(true)
const isChild = ref<boolean>(false)

type LookupState = 'disabled' | 'enabled' | 'ok' | 'use'
const lookupState = ref('enabled' as LookupState)

const fillData = () => {
  lookupState.value = 'disabled'
  emits('onNext')
}

const resetPersonData = () => {
  modelMasked.value = createPerson()
  modelPerson.value.useLookup = false
}

const getBirthdateFromSsn = (ssn: string) => {
  if (Personnummer.valid(ssn)) {
    const parsedSsn = Personnummer.parse(ssn)
    const birthDate = parsedSsn.getDate()
    const age = new Date().getFullYear() - birthDate.getFullYear()
    return age >= 0 ? (birthDate.toISOString().split('T')[0] as string) : ''
  } else {
    return '' // Reset birthDate if ssn is invalid
  }
}

// Writing the formatted SSN back re-runs the watcher; this lets it skip that one run
// instead of looking the same person up twice.
let selfFormattedSsn: string | null = null

const handleValidSsn = useDebounceFn(async (ssn: string) => {
  showLoader.value = true
  // Format SSN to a standard format
  const formatted = Personnummer.parse(ssn).format(true)
  if (formatted !== ssn) {
    selfFormattedSsn = formatted
    modelPerson.value.ssn = formatted
  }
  // Assign birthDate based on the SSN for validation purposes
  modelPerson.value.birthDate = getBirthdateFromSsn(ssn)
  // Fetch lookup result for the SSN
  // A dropped request falls back to manual entry, like a miss, instead of leaving the loader up.
  const lookupResult = zSsnLookupRes.parse(
    await $fetch('/api/lookup/ssn', { method: 'POST', body: { ssn } }).catch(() => ({ success: false })),
  )
  hasResult.value = lookupResult.success

  showLoader.value = false

  // Update the useLookup flag based on the lookup result
  modelPerson.value.useLookup = lookupResult.success
  // Update the lookup state based on the lookup result
  lookupState.value = lookupResult.success ? 'use' : 'enabled'

  // If lookup is successful, update the masked person data with the fetched name and address
  // else reset the person data
  if (lookupResult.success) {
    const { name, address } = lookupResult
    modelMasked.value = createPerson({ name, address, contacts: {} })
  } else {
    resetPersonData()
  }
}, 500)

const stopWatcherSsn = watch(
  () => modelPerson.value.ssn,
  async ssn => {
    const isOwnFormatting = ssn === selfFormattedSsn
    selfFormattedSsn = null
    if (isOwnFormatting) return

    if (lookupState.value !== 'disabled') lookupState.value = 'enabled' // reset state

    // Handle invalid SSN
    if (!Personnummer.valid(ssn)) {
      modelPerson.value.birthDate = ''
      resetPersonData()
      lookupState.value = 'disabled'
      showLoader.value = false
      return
    }

    // Handle age validation if required
    if (props.requireLegalAge) {
      const legalAge = validateLegalAge(ssn)
      isLegalAge.value = !legalAge.minor
      isChild.value = legalAge.child
      if (legalAge.minor) {
        resetPersonData()
        lookupState.value = 'disabled'
        showLoader.value = false
        return
      }
    }
    isLegalAge.value = true
    await handleValidSsn(ssn)
  },
)

const stopWatcherLookupState = watch(lookupState, val => {
  modelPerson.value.useLookup = val === 'use' ? true : false
})

const stopWatcherUseSsn = watch(useSsn, val => {
  if (!val) {
    modelMasked.value = createPerson()
    modelPerson.value.useLookup = false
    modelPerson.value.ssn = ''
  }
})

const handleClickNext = () => {
  if (props.v$) {
    props.v$.$touch()
    props.v$.$validate()
    if (!props.v$.$invalid) emits('onNext') // Emit the next event if valid
    return
  }
  emits('onNext')
}

onBeforeUnmount(() => {
  stopWatcherSsn()
  stopWatcherLookupState()
  stopWatcherUseSsn()
})

const inputVariants = cva('', {
  variants: {
    theme: {
      [THEMES.DEFAULT]:
        'border border-blue-dark focus:border-blue-dark shadow-[0_12px_33px_0_rgba(5, 58, 65, 0.04)] w-full',
      [THEMES.RDK]: 'border border-rdk-orange focus:border-rdk-orange w-full',
      [THEMES.DJURENS_RATT]:
        'border border-blue-dark focus:border-blue-dark shadow-[0_12px_33px_0_rgba(5, 58, 65, 0.04)] w-full',
    },
  },
  defaultVariants: {
    theme: THEMES.DEFAULT,
  },
})

const formVariants = cva('xl:max-w-[575px] w-full', {
  variants: {
    theme: {
      [THEMES.DEFAULT]: '',
      [THEMES.RDK]:
        'text-purple-act-dark [&_label]:text-purple-act-dark font-avenir [&_label]:text-body',
      [THEMES.DJURENS_RATT]: 'text-black [&_label]:text-black mb-8',
    },
  },
  defaultVariants: {
    theme: THEMES.DEFAULT,
  },
})

const buttonFillVariants = cva(
  'underline lg:my-9 lg:w-auto font-bold my-7 w-full text-center text-[14px] lg:text-base',
  {
    variants: {
      theme: {
        [THEMES.DEFAULT]: 'text-[#0088A4]',
        [THEMES.RDK]: 'text-purple-act-dark ',
        [THEMES.DJURENS_RATT]: 'text-[#0088A4] ',
      },
    },
    defaultVariants: {
      theme: THEMES.DEFAULT,
    },
  },
)

const ssnButtonVariant = cva('mb-4 inline text-sm  underline', {
  variants: {
    theme: {
      [THEMES.DEFAULT]: 'text-blue',
      [THEMES.RDK]: 'font-avenir text-purple-act-dark text-[16px] lg:text-[18px] mb-8 lg:mb-12',
      [THEMES.DJURENS_RATT]: 'text-blue',
    },
  },
  defaultVariants: {
    theme: THEMES.DEFAULT,
  },
})

const inputErrorVariants = cva('relative bg-transparent', {
  variants: {
    theme: {
      [THEMES.DEFAULT]: '',
      [THEMES.RDK]: 'text-[16px] lg:text-[18px]',
      [THEMES.DJURENS_RATT]: '',
    },
  },
  defaultVariants: {
    theme: THEMES.DEFAULT,
  },
})
</script>
